import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_LINKS, SITE_NAME } from "@/utils/site";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const word = keyword.trim();
    if (!word) return;
    navigate(`/search?q=${encodeURIComponent(word)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/category/movie") return location.pathname === "/category/movie";
    if (to === "/category/tv") return location.pathname === "/category/tv";
    if (to === "/category/movie/filter") return location.pathname.endsWith("/filter");
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent ? "bg-transparent" : "bg-primary-950/95 backdrop-blur"
      }`}
    >
      <div className="flex h-16 items-center justify-between gap-3 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500">
            <i className="ri-play-circle-fill text-lg text-primary-950" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[15px] font-bold tracking-wide text-background-50 md:text-base">
              {SITE_NAME}
            </span>
            <span className="mt-1 hidden text-[11px] tracking-[0.18em] text-background-300 sm:block">
              高清中文字幕 · 免费在线观看
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(link.to)
                  ? "bg-primary-800/80 text-accent-400"
                  : "text-background-200 hover:bg-primary-900/70 hover:text-background-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="hidden min-w-0 flex-1 items-center justify-end md:flex lg:max-w-[320px]">
          <div className="flex w-full items-center gap-2 rounded-md border border-primary-800 bg-primary-900/70 px-3 py-1.5 focus-within:border-accent-500">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              <i className="ri-search-line text-sm text-background-300" aria-hidden="true"></i>
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、导演、演员"
              aria-label="搜索影视作品"
              className="w-full min-w-0 bg-transparent text-sm text-background-50 placeholder:text-background-400 focus:outline-none"
            />
            <button
              type="submit"
              className="shrink-0 cursor-pointer whitespace-nowrap rounded bg-accent-500 px-2.5 py-1 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label={menuOpen ? "收起菜单" : "展开菜单"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-primary-800 text-background-100 lg:hidden"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl`} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-primary-800 bg-primary-950 px-4 pb-5 pt-3 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-md border border-primary-800 bg-primary-900 px-3 py-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              <i className="ri-search-line text-sm text-background-300" aria-hidden="true"></i>
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品"
              aria-label="搜索影视作品"
              className="min-w-0 flex-1 bg-transparent text-sm text-background-50 placeholder:text-background-400 focus:outline-none"
            />
            <button type="submit" className="shrink-0 cursor-pointer whitespace-nowrap rounded bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2.5 text-sm ${
                  isActive(link.to)
                    ? "bg-primary-800 text-accent-400"
                    : "bg-primary-900/60 text-background-200"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}